/**
 * Where can this product be bought, in this market? One verdict, computed
 * here and read by every surface that shows a way to buy: the card's CTA
 * (external-link.tsx), its structured data (product-schema.tsx), the link
 * writers (search-amazon-data.ts) and the page's own link check
 * (present-product.ts).
 *
 *   'retail'        today's behaviour: stores, and the market's Amazon search
 *                   when no store is known (Olivier, 2026-09-21).
 *   'dtc'           a brand sold only on its own site (DTC_STORES).
 *   'clinic'        sold and fitted by a professional after an exam
 *                   (prescription hearing aids): no Amazon search, no Buy
 *                   button on anything but a priced offer from a store in
 *                   this market.
 *   'not-in-market' not sold in this market at all: no Buy, no search.
 *
 * Why (QA 2026-09-28): hearing-aid cards sent shoppers to amazon.ca searches
 * returning Picasso art books and chargers, and a card the page itself called
 * "US-only, not sold in Canada" still had "Buy Now · Our pick".
 *
 * Pure and client-safe, never throws, and 'retail' whenever in doubt.
 */

import {
  CLINIC_ACCESSORY_WORDS,
  CLINIC_BRANDS,
  CLINIC_LINES,
  DTC_STORES,
  HEARING_AID_PAGE_SLUGS,
  HEARING_AID_TITLE_PHRASES,
  MARKET_ONLY_LINES,
  type ClinicKind,
  type DtcStore,
} from "../data/purchase-channels";

export type PurchaseChannel = "retail" | "dtc" | "clinic" | "not-in-market";

/** The product fields the verdict reads. */
export interface PurchaseChannelProduct {
  title?: string | null;
  brandName?: string | null;
  availability?: { status?: string | null } | null;
  externalLinks?: { href?: string | null; type?: string | null } | null;
}

export interface PurchaseChannelContext {
  /** The page's market ("CA", "UK", ...), from the URL, never the visitor's IP. */
  regionCode?: string | null;
  /** The page's own path, a supporting signal only (a hearing-aid page). */
  pagePath?: string | null;
}

/** Lower-case words, accents removed, "&" and punctuation as spaces. */
function words(text: string | null | undefined): string[] {
  return (text ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function phraseIn(haystack: readonly string[], phrase: string): boolean {
  const needle = phrase.split(" ");
  for (let i = 0; i + needle.length <= haystack.length; i++) {
    if (needle.every((word, j) => haystack[i + j] === word)) return true;
  }
  return false;
}

function isHearingAidPage(pagePath: string | null | undefined): boolean {
  const path = (pagePath ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
  return !!path && HEARING_AID_PAGE_SLUGS.some((slug) => path.includes(slug));
}

/**
 * The clinic that sells and fits this product, or null. Brand AND line, in
 * the product's own name; on a hearing-aid page, or with "hearing aid" in its
 * name, the brand alone. Never an accessory (a charger, domes, a TV streamer).
 */
export function clinicKind(product: PurchaseChannelProduct, context: PurchaseChannelContext = {}): ClinicKind | null {
  try {
    const title = words(product?.title);
    if (title.length === 0) return null;
    if (title.some((word) => CLINIC_ACCESSORY_WORDS.has(word))) return null;
    const brandWords = new Set([...title, ...words(product?.brandName)]);
    if (![...brandWords].some((word) => CLINIC_BRANDS.has(word))) return null;
    const code = (context.regionCode ?? "").toUpperCase();
    const hearingContext =
      isHearingAidPage(context.pagePath) || HEARING_AID_TITLE_PHRASES.some((phrase) => phraseIn(title, phrase));
    for (const entry of CLINIC_LINES) {
      if (entry.markets && code && !entry.markets.includes(code)) continue;
      if (!entry.brands.some((brand) => brandWords.has(brand))) continue;
      if (hearingContext || entry.lines.some((line) => title.includes(line))) return entry.kind;
    }
    return null;
  } catch {
    return null;
  }
}

/** The markets a market-only line is sold in, or null when the product is no such line. */
export function soldOnlyIn(product: PurchaseChannelProduct): readonly string[] | null {
  const title = words(product?.title);
  const brandWords = new Set([...title, ...words(product?.brandName)]);
  const entry = MARKET_ONLY_LINES.find(
    (line) => line.brands.some((brand) => brandWords.has(brand)) && line.lines.some((name) => title.includes(name)),
  );
  return entry ? entry.soldIn : null;
}

/**
 * The registry key of a brand sold only on its own site (DTC_STORES), or
 * null. Matched on the brand, or as the LEADING word(s) of the title, which
 * keeps common nouns out ("…Framesets", "Agile framework guide…").
 */
export function dtcBrandKey(product: { title?: string | null; brandName?: string | null }): string | null {
  const brand = (product?.brandName ?? "").trim().toLowerCase();
  const title = (product?.title ?? "").trim().toLowerCase();
  for (const key of Object.keys(DTC_STORES)) {
    if (brand === key || title === key || title.startsWith(`${key} `)) return key;
  }
  return null;
}

/** The store of a brand sold only on its own site, or null. */
export function dtcStoreFor(product: { title?: string | null; brandName?: string | null }): DtcStore | null {
  const key = dtcBrandKey(product);
  return key ? DTC_STORES[key] : null;
}

/**
 * The enrichment pipeline found the product not purchasable in this market
 * (`availability.status`), and no real link has resolved since: the stored
 * link is absent or the market's Amazon search. A real listing wins over a
 * stale flag.
 */
function flaggedNotInMarket(product: PurchaseChannelProduct): boolean {
  if (product?.availability?.status !== "unavailable-in-country") return false;
  const link = product.externalLinks;
  return !link?.href || link.type === "amazon-search";
}

export function purchaseChannel(
  product: PurchaseChannelProduct | null | undefined,
  context: PurchaseChannelContext = {},
): PurchaseChannel {
  try {
    if (!product) return "retail";
    const code = (context.regionCode ?? "").toUpperCase();
    if (flaggedNotInMarket(product)) return "not-in-market";
    const soldIn = soldOnlyIn(product);
    if (soldIn && code && !soldIn.includes(code)) return "not-in-market";
    if (clinicKind(product, context)) return "clinic";
    const store = dtcStoreFor(product);
    if (store && (!store.markets || !code || store.markets.includes(code))) return "dtc";
    return "retail";
  } catch {
    return "retail";
  }
}
